/**
 * StaffDashboardPage (Phase-2, Module 1 — LokBhavan staff landing)
 *
 * Staff select an officer and act on their behalf: upload medical reports /
 * prescriptions / documents, request admissions & investigations. The chosen
 * officer id is passed via ?officerId= to the shared officer pages, which
 * `useCurrentOfficer` honors when present.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Card, CardActionArea, CardContent, Typography, FormControl, InputLabel, Select, MenuItem, Alert } from '@mui/material';
import {
  Description as ReportIcon, MedicationLiquid as RxIcon, Folder as DocIcon,
  LocalHospital as AdmIcon, Biotech as InvIcon, Badge as StaffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { PageScaffold } from '../../components/common';
import { fetchOfficers } from '../../store/slices/officerSlice';
import { RootState, AppDispatch } from '../../store';

interface OfficerRow { id: number; fullName: string; designation?: string; department?: string; cadreDisplayName?: string; }

const ACTIONS = [
  { key: 'report', label: 'Upload Medical Report', desc: 'PDF / image reports', icon: <ReportIcon />, path: '/officer/medical-reports', color: '#3B82F6' },
  { key: 'rx', label: 'Upload Prescription', desc: 'Multi-source prescription', icon: <RxIcon />, path: '/prescription-documents', color: '#8B5CF6' },
  { key: 'doc', label: 'Medical Document', desc: 'Repository document', icon: <DocIcon />, path: '/officer/documents', color: '#6366F1' },
  { key: 'adm', label: 'Request Admission', desc: 'IPD admission', icon: <AdmIcon />, path: '/officer/admissions', color: '#F59E0B' },
  { key: 'inv', label: 'Request Investigation', desc: 'Lab investigation', icon: <InvIcon />, path: '/officer/investigations', color: '#EC4899' },
];

const StaffDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const officersState = useSelector((s: RootState) => s.officer);
  const [selected, setSelected] = useState<number | ''>('');

  useEffect(() => { dispatch(fetchOfficers() as any); }, [dispatch]);

  const officers: OfficerRow[] = useMemo(() => {
    const raw = officersState.officers as unknown;
    return Array.isArray(raw) ? (raw as OfficerRow[]) : ((raw as any)?.content as OfficerRow[]) || [];
  }, [officersState.officers]);

  const go = (path: string) => {
    if (!selected) return;
    navigate(`${path}?officerId=${selected}`);
  };

  return (
    <PageScaffold title="Staff Dashboard" subtitle="LokBhavan staff — act on behalf of an officer" icon={<StaffIcon />}>
      <FormControl fullWidth sx={{ mb: 3, maxWidth: 420 }}>
        <InputLabel>Select Officer</InputLabel>
        <Select label="Select Officer" value={selected} onChange={(e) => setSelected(Number(e.target.value))}>
          {officers.map((o) => <MenuItem key={o.id} value={o.id}>{o.fullName} — {o.designation || o.department || o.cadreDisplayName || 'Officer'}</MenuItem>)}
        </Select>
      </FormControl>

      {!selected && <Alert severity="info" sx={{ mb: 3 }}>Select an officer above to enable healthcare actions on their behalf.</Alert>}

      <Grid container spacing={2}>
        {ACTIONS.map((a) => (
          <Grid item xs={12} sm={6} md={4} key={a.key}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${a.color}`, opacity: selected ? 1 : 0.6 }}>
              <CardActionArea disabled={!selected} onClick={() => go(a.path)}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ color: a.color }}>{a.icon}</Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{a.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{a.desc}</Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageScaffold>
  );
};

export default StaffDashboardPage;